/**
 * MyRunnersV2 Component
 * My Runners page - track favorites during the race
 * Route: /v2/:slug/my-runners
 *
 * Desktop: Pencil `7b5eI`
 * Mobile: Pencil `JbiHd`
 */

import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useRaceData } from '../../hooks/useRaceData';
import { useRunnerUpdates } from '../../hooks/useRunnerUpdates';
import { useFollowedRunners } from '../../hooks/useFollowedRunners';

import { RaceHeaderV2 } from './RaceHeaderV2';
import { RunnerAvatarV2 } from './RunnerAvatarV2';
import { StatusBadgeV2 } from './StatusBadgeV2';
import { AddRunnersModal } from './AddRunnersModal';

export function MyRunnersPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const { race, loading: raceLoading, error: raceError } = useRaceData(slug || '');
  // This page never renders feedEvents, so disable feed fetching to avoid extra load.
  const { runners } = useRunnerUpdates(race?.id || null, { enabled: !!race, includeFeed: false });
  const { followedBibs, toggleFollow, removeFollow } = useFollowedRunners(slug || '');

  const [showAddModal, setShowAddModal] = useState(false);

  // Get followed runner data
  const followedRunners = useMemo(() => {
    return runners.filter(r => followedBibs.has(r.bib));
  }, [runners, followedBibs]);

  if (raceError) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--v2-text-secondary)' }}>Race not found</div>
      </div>
    );
  }

  // Order matters: useRaceData reports errors with race=null and loading=false.
  // If we check !race first, the page gets stuck on "Loading..." forever.
  if (raceLoading || !race) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--v2-text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="v2-tarawera">
      {/* Nav bar */}
      <RaceHeaderV2 race={race} lastUpdated={null} />

      {/* Dark hero */}
      <div className="v2-my-runners-hero">
        <Link to={`/v2/${slug}`} className="v2-detail-back" style={{ justifyContent: 'center', marginBottom: 24 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Race
        </Link>

        <div className="v2-my-runners-title">MY RUNNERS</div>
        <div className="v2-my-runners-subtitle">
          Track your favorites during the race.
        </div>
        <button
          className="v2-add-runners-btn"
          onClick={() => setShowAddModal(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          ADD RUNNERS
        </button>
      </div>

      {/* Runner cards (light section) */}
      <div className="v2-followed-list">
        {followedRunners.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--v2-text-muted)' }}>
            <p style={{ fontSize: 14, marginBottom: 8 }}>No runners followed yet.</p>
            <p style={{ fontSize: 12 }}>
              Click "+ ADD RUNNERS" to start tracking your favorites.
            </p>
          </div>
        ) : (
          followedRunners.map((runner) => (
            <div
              key={runner.id}
              className="v2-followed-card"
              onClick={() => navigate(`/v2/${slug}/runner/${runner.id}`)}
            >
              <RunnerAvatarV2
                firstName={runner.first_name}
                lastName={runner.last_name}
              />
              <div className="v2-followed-card-info">
                <div className="v2-followed-card-name">
                  {runner.first_name} {runner.last_name}
                </div>
                <div className="v2-followed-card-meta">
                  BIB {runner.bib}
                  {runner.gender && ` · ${runner.gender}`}
                  {runner.overall_place && ` · #${runner.overall_place} Overall`}
                </div>
              </div>
              <StatusBadgeV2 status={runner.status} label={runner.status_short_label} />
              <button
                className="v2-followed-card-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFollow(runner.bib);
                }}
                title="Remove from My Runners"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add Runners Modal */}
      {showAddModal && (
        <AddRunnersModal
          runners={runners}
          followedBibs={followedBibs}
          onToggleFollow={toggleFollow}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
